/**
 * ScoreAggregationRuleService — rules that aggregate over numeric scores.
 *
 * The categorical rule services (mention/threshold/zero-tolerance) COUNT
 * events. Score-aggregation rules instead run a predicate against the numeric
 * value an event carries — `avg < 50`, "3 fails this term", "2 in a row below" —
 * which is what the Academics (marks) domain needs.
 *
 * This service is purely additive: it only acts on rules of type
 * `score-aggregation`. Discipline has none, so its evaluation path is
 * untouched (and RuleEvaluator gates this step to non-discipline domains
 * anyway). See knowledge/wiki/features/marks-domain.md + extractable-domains.md.
 *
 * Cadence: categorical rules fire once per event via the per-event
 * `processedForRules` flag. That can't model aggregates — a new mark changes
 * the average, so a rule that didn't fire on mark 2 may fire on mark 3.
 * Instead we keep a per-rule-per-student-per-window ledger:
 *   schools/{schoolId}/{domainId}_ruleFires/{ruleId}_{studentId}_{windowKey}
 * One ledger doc = one fire; its presence means the rule already triggered for
 * that student in that window and won't re-fire.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { academicCalendarService } from '../../academicCalendarService';
import { disciplineLogger } from '../../../utils/disciplineLogger';

const DATE_TIMEFRAMES = new Set(['academic-term', 'academic-semester', 'academic-year']);

export class ScoreAggregationRuleService {
  /**
   * @param {Object} dependencies
   * @param {Object} dependencies.ruleService - active-rules source
   * @param {Object} dependencies.mentionService - the GenericEventService (events)
   * @param {Object} dependencies.consequenceService - the GenericActionService (actions)
   * @param {string} dependencies.domainId - owning domain id (e.g. 'academics')
   */
  constructor(dependencies = {}) {
    this.ruleService = dependencies.ruleService;
    this.mentionService = dependencies.mentionService;
    this.consequenceService = dependencies.consequenceService;
    this.domainId = dependencies.domainId || 'academics';
  }

  /**
   * Entry point — evaluate every active score-aggregation rule for the student
   * who just received a new mark.
   * @param {Object} event - the just-created score event
   * @param {Object} studentData - pre-fetched data ({ mentions: domain events })
   */
  async processScoreAggregationRules(event, studentData) {
    try {
      const schoolId = event.schoolId || studentData?.schoolId;
      if (!schoolId) return;

      const activeRules = await this.ruleService.getActiveRules(schoolId);
      const scoreRules = (activeRules || []).filter((r) => r.type === 'score-aggregation' && r.active !== false);
      if (scoreRules.length === 0) return;

      // The student's marks for THIS domain. studentData.mentions is the
      // domain-events slice; merge in the just-created event in case the cache
      // predates it (it was written milliseconds ago).
      const marks = this.collectMarks(event, studentData);

      for (const rule of scoreRules) {
        await this.evaluateRule(rule, event, marks, schoolId);
      }
    } catch (error) {
      // Never let a rule failure break event creation.
      disciplineLogger.error?.('Score-aggregation processing failed', { error: error.message });
      console.error('[ScoreAggregationRuleService] processing failed:', error?.message || error);
    }
  }

  /** Build the student's mark list, guaranteeing the current event is present. */
  collectMarks(event, studentData) {
    const list = Array.isArray(studentData?.mentions) ? [...studentData.mentions] : [];
    if (event?.id && !list.some((m) => m.id === event.id)) {
      list.push(event);
    } else if (!event?.id) {
      list.push(event);
    }
    return list;
  }

  /** Numeric value a rule targets: a sub-rubric key if set, else the headline score. */
  scoreOf(mark, subRubricKey) {
    if (subRubricKey) {
      if (mark?.scores && typeof mark.scores[subRubricKey] === 'number') return mark.scores[subRubricKey];
      // Sub-rubrics may also ride the canonical fieldValues map.
      if (mark?.fieldValues && typeof mark.fieldValues[subRubricKey] === 'number') return mark.fieldValues[subRubricKey];
    }
    // Number-mode events persist the headline score at fieldValues.score — the
    // engine writes the primary field (`score`) into the fieldValues map, not a
    // top-level numericValue. Direct/legacy writes may carry it top-level as
    // numericValue or score, so check every canonical location.
    const raw = mark?.numericValue ?? mark?.score ?? mark?.fieldValues?.score;
    return typeof raw === 'number' ? raw : null;
  }

  /** Crossing predicate. `below` → strictly less; `above` → strictly greater. */
  crosses(value, threshold, direction) {
    if (value === null || typeof threshold !== 'number') return false;
    return direction === 'below' ? value < threshold : value > threshold;
  }

  /** Milliseconds for a mark's chronological position (createdAt, then issuedAt). */
  timeOf(mark) {
    const d = mark?.createdAt || mark?.issuedAt;
    if (!d) return 0;
    if (d.toDate) return d.toDate().getTime();
    if (d instanceof Date) return d.getTime();
    const parsed = new Date(d).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Evaluate a single score-aggregation rule (or a compound of conditions)
   * against every subject partition the rule's scope produces.
   */
  async evaluateRule(rule, event, marks, schoolId) {
    try {
      const isCompound = rule.shape === 'compound';

      // Partition the marks per the rule's scope. 'same-subject' yields one
      // partition per subject (so each failing subject fires independently);
      // 'all-subjects' pools; { subjectIds } restricts to a list.
      const partitions = isCompound
        ? [{ key: 'compound', marks }] // compound resolves scope per-condition
        : this.partition(rule, event, marks);

      for (const part of partitions) {
        const result = isCompound
          ? await this.evaluateCompound(rule, event, marks, schoolId)
          : await this.evaluateCondition(rule, part.marks, schoolId, part.key);

        if (!result.triggered) continue;

        const windowKey = isCompound ? `compound:${result.windowKey}` : `${part.key}:${result.windowKey}`;
        await this.fireOnce(rule, event, schoolId, windowKey, {
          subjectId: isCompound ? null : part.subjectId,
          subject: isCompound ? null : part.subject,
        });
      }
    } catch (error) {
      console.error('[ScoreAggregationRuleService] evaluateRule failed:', error?.message || error);
    }
  }

  /**
   * Split marks into evaluation partitions based on subjectScope.
   * @returns {Array<{key, subjectId, subject, marks}>}
   */
  partition(rule, event, marks) {
    const scope = rule.subjectScope;

    if (scope === 'all-subjects') {
      return [{ key: 'all', subjectId: null, subject: null, marks }];
    }

    if (scope && typeof scope === 'object' && Array.isArray(scope.subjectIds)) {
      const allow = new Set(scope.subjectIds);
      const filtered = marks.filter((m) => allow.has(m.subjectId));
      return [{ key: scope.subjectIds.join('+'), subjectId: null, subject: null, marks: filtered }];
    }

    // Default 'same-subject' — one partition per subject the student has marks in.
    const bySubject = new Map();
    for (const m of marks) {
      const sid = m.subjectId || 'unknown';
      if (!bySubject.has(sid)) bySubject.set(sid, { subject: m.subject || '', marks: [] });
      bySubject.get(sid).marks.push(m);
    }
    return Array.from(bySubject.entries()).map(([subjectId, { subject, marks: subjectMarks }]) => ({
      key: subjectId,
      subjectId,
      subject,
      marks: subjectMarks,
    }));
  }

  /**
   * Evaluate one (non-compound) condition over an already-partitioned set of
   * marks. Returns { triggered, windowKey }.
   */
  async evaluateCondition(condition, partitionMarks, schoolId, partitionKey) {
    const { window, windowKey } = await this.resolveWindow(condition, partitionMarks, schoolId, partitionKey);
    if (window.length === 0) return { triggered: false, windowKey };

    const [shapeName, direction] = String(condition.shape || '').split('-'); // e.g. 'avg','below'
    const subRubricKey = condition.subRubricKey || null;
    const threshold = condition.threshold;
    const values = window.map((m) => this.scoreOf(m, subRubricKey)).filter((v) => v !== null);
    if (values.length === 0) return { triggered: false, windowKey };

    let triggered = false;

    if (shapeName === 'avg') {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      triggered = this.crosses(avg, threshold, direction);
    } else if (shapeName === 'count') {
      const n = values.filter((v) => this.crosses(v, threshold, direction)).length;
      triggered = n >= (condition.count || 1);
    } else if (shapeName === 'streak') {
      // The last `count` marks (chronological) must all cross the threshold.
      const need = condition.count || 1;
      const ordered = [...window].sort((a, b) => this.timeOf(a) - this.timeOf(b));
      const tail = ordered.slice(-need);
      triggered =
        tail.length >= need &&
        tail.every((m) => this.crosses(this.scoreOf(m, subRubricKey), threshold, direction));
    }

    return { triggered, windowKey };
  }

  /** Compound rule: every nested condition must independently trigger (ANDed). */
  async evaluateCompound(rule, event, allMarks, schoolId) {
    const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
    if (conditions.length === 0) return { triggered: false, windowKey: 'none' };

    const keys = [];
    for (const cond of conditions) {
      // Each condition resolves its OWN scope against the full mark set.
      const parts = this.partition(cond, event, allMarks);
      // A condition is satisfied if ANY of its partitions trigger.
      let condTriggered = false;
      let condKey = 'none';
      for (const part of parts) {
        const r = await this.evaluateCondition(cond, part.marks, schoolId, part.key);
        if (r.triggered) {
          condTriggered = true;
          condKey = `${part.key}:${r.windowKey}`;
          break;
        }
      }
      if (!condTriggered) return { triggered: false, windowKey: 'none' };
      keys.push(condKey);
    }

    return { triggered: true, windowKey: keys.join('|') };
  }

  /**
   * Resolve the rule's window over a partition's marks.
   * @returns {{ window: Array, windowKey: string }}
   */
  async resolveWindow(condition, partitionMarks, schoolId, partitionKey) {
    const timeframe = condition.timeframe;

    if (timeframe === 'last-N-marks') {
      const n = condition.timeCount || 1;
      const ordered = [...partitionMarks].sort((a, b) => {
        const t = this.timeOf(a) - this.timeOf(b);
        return t !== 0 ? t : String(a.id || '').localeCompare(String(b.id || ''));
      });
      return { window: ordered.slice(-n), windowKey: `${partitionKey}:last-${n}` };
    }

    if (DATE_TIMEFRAMES.has(timeframe)) {
      const range = await academicCalendarService.getAcademicDateRange(schoolId, timeframe, condition.timeCount || 1);
      if (!range) {
        // No calendar configured — fall back to all marks so the rule still works.
        return { window: partitionMarks, windowKey: `${timeframe}:nocal` };
      }
      const startMs = range.start instanceof Date ? range.start.getTime() : new Date(range.start).getTime();
      const endMs = range.end instanceof Date ? range.end.getTime() : new Date(range.end).getTime();
      const window = partitionMarks.filter((m) => {
        const t = this.timeOf(m);
        return t >= startMs && t <= endMs;
      });
      const windowKey = await this.dateWindowKey(timeframe, schoolId, range);
      return { window, windowKey };
    }

    // Unknown timeframe — evaluate over everything, keyed stably.
    return { window: partitionMarks, windowKey: `all:${timeframe || 'none'}` };
  }

  /**
   * Canonical key for a date window. Prefer the current term's own id so a new
   * term starts a fresh ledger entry; term IDs are Firestore-auto, never a
   * hand-built "2026-T1" string.
   */
  async dateWindowKey(timeframe, schoolId, range) {
    if (timeframe === 'academic-term') {
      try {
        const term = await academicCalendarService.getCurrentTerm(schoolId);
        if (term?.id) return `term-${term.id}`;
        if (term?.name) return `term-${term.name}`;
      } catch (_) { /* fall through to date key */ }
    }
    const startMs = range.start instanceof Date ? range.start.getTime() : new Date(range.start).getTime();
    return `${timeframe}-${startMs}`;
  }

  /**
   * Fire the rule's action exactly once per window via the ledger. Writes the
   * follow-up action record (carrying the rule's `childDomain` valency) and
   * marks the ledger so the rule won't re-fire for this student/window.
   */
  async fireOnce(rule, event, schoolId, windowKey, extra = {}) {
    const ledgerCollection = `${this.domainId}_ruleFires`;
    const ledgerId = `${rule.id}_${event.studentId}_${windowKey}`;
    const ledgerRef = doc(collection(db, 'schools', schoolId, ledgerCollection), ledgerId);

    const existing = await getDoc(ledgerRef);
    if (existing.exists()) {
      disciplineLogger.debug?.('Score rule already fired for window — skipping', { ledgerId });
      return false;
    }

    const childDomain = rule.childDomain || 'mentions';
    const now = Timestamp.now();

    // The follow-up action (the academics equivalent of a discipline
    // consequence). childDomain declares valency: support (mention) vs
    // achievement (recognition).
    let actionId = null;
    try {
      const action = await this.consequenceService.createDisciplineConsequence({
        schoolId,
        domainId: this.domainId,
        studentId: event.studentId,
        studentName: event.studentName || '',
        className: event.className || '',
        grade: event.grade || '',
        ruleId: rule.id,
        ruleTitle: rule.title || rule.name || '',
        ruleType: 'score-aggregation',
        childDomain,
        consequenceType: rule.consequenceType || null,
        subjectId: extra.subjectId || null,
        subject: extra.subject || null,
        windowKey,
        triggerEventId: event.id || null,
        status: 'pending',
        createdAt: now,
        issuedAt: now,
      });
      actionId = action?.id || null;
    } catch (error) {
      console.error('[ScoreAggregationRuleService] action creation failed:', error?.message || error);
      // Still write the ledger so we don't spam retries on every subsequent mark.
    }

    await setDoc(ledgerRef, {
      ruleId: rule.id,
      studentId: event.studentId,
      windowKey,
      childDomain,
      actionId,
      firedAt: now,
    });

    disciplineLogger.info?.('🎯 SCORE RULE FIRED', {
      rule: rule.title || rule.id,
      studentId: event.studentId,
      windowKey,
      childDomain,
    });
    return true;
  }
}
